import React from 'react'
import { Label, Input, FormFeedback } from 'reactstrap'
import {
  Controller,
  FieldValues,
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormSetError,
} from 'react-hook-form'
import ICSToolTip from '../ICSToolTip/ICSToolTip'
import './ICSSelectInput.css'

const ICSSelectInput = ({
  name,
  id = `${name}Input`,
  control,
  errors,
  defaultValue = '',
  placeholder = 'Select',
  helpText = '',
  isSubmitted,
  options = [],
  autoComplete = 'off',
  label,
}: ICSSelectInputProps) => (
  <Controller
    control={control}
    name={name}
    defaultValue={defaultValue}
    render={({
      field: { onChange, onBlur, value, name, ref },
      fieldState: { invalid, isTouched, isDirty, error },
    }) => {
      return (
        <>
          <Label for={id} className='input-label'>
            {label}
            {helpText && (
              <ICSToolTip
                id={`${name}ToolTip`}
                text={helpText}
                target={`#${name}ToolTip`}
              />
            )}
          </Label>
          <Input
            type='select'
            name={name}
            id={id}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            value={value}
            valid={
              !invalid && (isTouched || isSubmitted) && value !== defaultValue
            }
            invalid={invalid && isTouched}
            innerRef={ref}
            autoComplete={autoComplete}
            className={
              !(isTouched || isDirty) ||
              (value === defaultValue && (isTouched || isDirty) && !invalid)
                ? ''
                : error === undefined &&
                  (isTouched || isDirty) &&
                  value !== defaultValue
                ? 'custom-valid-select'
                : 'custom-invalid-select'
            }
          >
            {placeholder && (
              <option id={placeholder} value={defaultValue}>
                -- {placeholder} --
              </option>
            )}

            {options &&
              options.map((option, i: number) => (
                <option
                  id={`${name}Option${i}`}
                  key={option.valueField}
                  value={option.valueField}
                >
                  {option.displayField}
                </option>
              ))}
          </Input>
          <FormFeedback
            id={`${name}Error`}
            valid={errors?.[name] ? false : true}
            role='alert'
          >
            {errors?.[name]?.message}
          </FormFeedback>
        </>
      )
    }}
  />
)

export default ICSSelectInput

type ICSSelectInputProps<TFieldValues extends FieldValues = FieldValues> = {
  id?: string
  name: string
  control?: Control<TFieldValues>
  errors?: FieldErrors<TFieldValues>
  defaultValue?: string
  placeholder?: string
  helpText?: string
  isSubmitted?: boolean
  autoComplete?: string
  options?: { valueField: string | number; displayField: string }[]
  setValue?: UseFormSetValue<FieldValues>
  setError?: UseFormSetError<FieldValues>
  label?: string
}
